import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ScopeContextStorage } from '@/shared/lib/scope-storage';

import {
  createNotificationSubscription,
  getNotificationSubscriptions,
  patchNotificationSubscription,
  type NotificationSubscription,
  type NotificationSubscriptionResponse,
  type NotificationType,
} from '../api/notification-subscription';

interface ToggleWrite {
  enable: boolean;
  onError?: (error: unknown) => void;
}

/**
 * Read + toggle helper for a single NotificationType. Today's only consumer
 * is the broker-order toggle on the Settings page; the same hook works for any type.
 *
 * Semantics:
 * - "enabled" = there is an active NotificationSubscription whose `types`
 *   includes ``type``.
 * - Enabling: create a new sub if none exists, otherwise PATCH the existing
 *   sub (re-add the type and flip back to active if needed).
 * - Disabling: PATCH to remove the type from the active sub. If that empties
 *   `types`, cancel the sub.
 */
export function useNotificationSubscriptionToggle(type: NotificationType) {
  const queryClient = useQueryClient();
  const sessionId = ScopeContextStorage.getCurrentSessionId();
  const scopeContext = sessionId ? ScopeContextStorage.getScopeContext(sessionId) : null;
  const queryKey = ['notification-subscriptions', sessionId];
  const mutationKey = ['notification-subscription-write', sessionId];

  const query = useQuery({
    queryKey,
    queryFn: () => getNotificationSubscriptions(sessionId!, scopeContext!),
    enabled: !!sessionId && !!scopeContext,
  });

  const storedSubscriptions = () =>
    queryClient.getQueryData<NotificationSubscriptionResponse>(queryKey)?.subscriptions;
  const activeSubscriptionIn = (subscriptions: NotificationSubscription[] | undefined) =>
    subscriptions?.find((s) => s.status === 'active') ?? null;

  const subscriptions = query.data?.subscriptions;
  const isKnown = subscriptions !== undefined;
  const isEnabled = !!activeSubscriptionIn(subscriptions)?.types.includes(type);
  const isSaving = useIsMutating({ mutationKey }) > 0;

  const mutation = useMutation({
    mutationKey,
    mutationFn: async ({ enable }: ToggleWrite) => {
      const current = storedSubscriptions();
      if (!sessionId || !scopeContext || current === undefined) return;
      const activeSubscription = activeSubscriptionIn(current);

      if (enable) {
        if (activeSubscription) {
          const nextTypes = Array.from(new Set([...activeSubscription.types, type]));
          return patchNotificationSubscription(sessionId, scopeContext, activeSubscription.id, {
            types: nextTypes,
            status: 'active',
          });
        }
        return createNotificationSubscription(sessionId, scopeContext, { types: [type] });
      }

      if (!activeSubscription) return;
      const remaining = activeSubscription.types.filter((t) => t !== type);
      if (remaining.length === 0) {
        return patchNotificationSubscription(sessionId, scopeContext, activeSubscription.id, {
          status: 'canceled',
        });
      }
      return patchNotificationSubscription(sessionId, scopeContext, activeSubscription.id, {
        types: remaining,
      });
    },
    onError: (error, { onError }) => {
      onError?.(error);
    },
    onSettled: () => queryClient.resetQueries({ queryKey, exact: true }),
  });

  return {
    isEnabled,
    isKnown,
    isLoading: query.isLoading,
    isSaving,
    isRetrying: query.isFetching,
    retry: () => query.refetch(),
    setEnabled: (value: boolean, options?: { onError?: (error: unknown) => void }) => {
      if (storedSubscriptions() === undefined || queryClient.isMutating({ mutationKey }) > 0) return;
      mutation.mutate({ enable: value, onError: options?.onError });
    },
  };
}
